import clsx from 'clsx'
import { Button, Container, Grid, Icon, Typography } from 'gu-ui-kit'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useRouteMatch } from 'react-router'
import { Link } from 'react-router-dom'
import formatMoney from 'utils/formatMoney'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import Logo from 'layouts/subcomponents/Logo'
import UserMenu from 'components/Header/subcomponents/UserMenu'
import styles from 'components/Header/header.module.scss'
import TicketsCounter from 'components/Header/subcomponents/TicketsCounter'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import { ROUTES } from 'constants/routes'

const Header = () => {
  const { auth } = useSelector(({ auth }) => auth)
  const {
    user: { balance },
  } = useSelector(({ app }) => app)
  const pathGen = useGeneratePathI18n()
  const matched = useRouteMatch()
  const breakpoints = useBreakpoint()
  const { t } = useTranslation(['common'])

  const isRegisterForm = useMemo(() => matched.path.match(/\/register/), [matched])

  return (
    <Container
      display={'flex'}
      component={'header'}
      className={clsx(styles['header'], styles['header_logged'])}
    >
      <Container
        display={'flex'}
        flexAlign={'center'}
        flexJustify={'space-between'}
        className={styles['header__width-wrapper']}
      >
        <Container
          component={'nav'}
          display={'flex'}
          flexAlign={'center'}
          className={styles['header__nav']}
        >
          <Container className={styles['header__logo']}>
            <Logo />
          </Container>

          {auth && (
            <>
              <Button
                className={styles['header__sidebar-toggle']}
                variant={'ghost'}
                grey
                onClick={() => global.dispatchEvent(new Event('sidebar:toggle'))}
                noLabel
                size={'m'}
                startIcon={<Icon name={'burger'} />}
              />
            </>
          )}
        </Container>

        <Grid
          wrap={false}
          gap={!breakpoints.mdl ? 32 : 16}
          display={'flex'}
          flexJustify={'flex-end'}
          flexAlign={'center'}
        >
          {auth ? (
            <>
              <Grid gap={8} wrap={false} fluid={false}>
                {!breakpoints.sm && (
                  <Button
                    component={'a'}
                    size={breakpoints.fb ? 'm' : 's'}
                    variant={'ghost'}
                    grey
                    color={'Interface-Eight'}
                    startIcon={<Icon name={'question'} />}
                    target={'_blank'}
                    href={pathGen(ROUTES.FAQ)}
                  />
                )}
                <TicketsCounter />
              </Grid>

              <Link to={pathGen(ROUTES.FINANCE_WALLETS)}>
                <Typography
                  className={styles['wallet-balance']}
                  component={'span'}
                  variant={breakpoints.sm ? 'Body-15' : 'Body-25'}
                  dangerouslySetInnerHTML={{
                    __html: balance && formatMoney(balance.amount, balance.currency),
                  }}
                />
              </Link>

              {!breakpoints.sm && (
                <Grid gap={16} wrap={false} fluid={false}>
                  <Button
                    noLabel={breakpoints.fb}
                    component={Link}
                    noWrap
                    title={t('common::user_menu.add_funds')}
                    size={breakpoints.fb ? 'm' : 's'}
                    variant={'primary'}
                    to={pathGen(ROUTES.BILLING_TOP_UP)}
                    startIcon={<Icon name={'plus'} />}
                  >
                    {t('common::user_menu.add_funds')}
                  </Button>

                  <Button
                    noLabel={breakpoints.fb}
                    grey
                    component={Link}
                    to={pathGen(ROUTES.FINANCE_WALLETS)}
                    size={breakpoints.fb ? 'm' : 's'}
                    variant={'secondary'}
                    startIcon={<Icon name={'card'} />}
                  >
                    <Typography component={'span'} variant={'Body-15'}>
                      {t('common::header.balance')}
                    </Typography>
                  </Button>
                </Grid>
              )}
              <UserMenu />
            </>
          ) : (
            <>
              <Typography variant={'Body-15'} color={'Label-Secondary'} noMargin>
                {isRegisterForm
                  ? t('common::header.has_account')
                  : t('common::header.have_no_account')}
              </Typography>
              <Link to={pathGen(isRegisterForm ? ROUTES.AUTH_LOGIN : ROUTES.AUTH_REGISTRATION)}>
                <Button
                  component={'span'}
                  className={clsx(styles['button'], styles['button-add'], {
                    [styles['button-add_compact']]: breakpoints.lg,
                  })}
                  size={'s'}
                  variant={'primary'}
                >
                  {t(
                    isRegisterForm
                      ? 'common::header.login_button_label'
                      : 'common::header.register_button_label',
                  )}
                </Button>
              </Link>
            </>
          )}
        </Grid>
      </Container>
    </Container>
  )
}

export default Header
